import {
	API_ENDPOINTS,
	DataTable,
	formatDate,
	FormInput,
	ImageType,
	PageLayout,
	route,
	useLangContext,
	StringHelpers,
	DateFormats,
	Variants,
	formatDateFns,
	checkTheme,
	Themes,
	Icon,
	Icons,
	useAuthorisationContext,
	Roles,
	FormInputType,
	DataColumnInterface,
	Permissions,
} from 'carrier-fe';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ViessmannInstallationListItem, ToshibaInstallationListItem } from '../../types/installation';
import { DropdownItemProps } from 'carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem';

function InstallationList() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { hasRoleAccess, hasPermissionAccess } = useAuthorisationContext();
	const { crud, fields, registration } = useLangContext();

	const handleOptionSelect = (
		action: string | ImageType | null,
		data: ViessmannInstallationListItem | ToshibaInstallationListItem
	) => {
		const { id = '' } = data;
		const actions: { [key: string]: () => void } = {
			view: () => {
				const searchFilter = searchParams.get('filter[search]');
				navigate(
					searchFilter
						? `/installation/${id}?filter[search]=${encodeURIComponent(searchFilter)}`
						: `/installation/${id}`
				);
			},
		};

		if (typeof action === 'string' && actions[action]) {
			actions[action]();
		}
	};

	const colMapping = (data: ViessmannInstallationListItem & ToshibaInstallationListItem): DataColumnInterface[] => {
		const addressDisplay = [
			data?.address_line_1,
			data?.town_city,
			data?.postcode_zipcode,
		]
			.filter(Boolean)
			.join(', ') || '-';
		const statusColumn: DataColumnInterface =
		{
			id: 'status',
			label: StringHelpers.title(
				fields?.status 
				|| 'status'
			),
			value: data?.status_display 
				|| '-',
			sortable: true,
		};
		const deletedColumn: DataColumnInterface = 
		{
			id: 'deleted_at',
			label: StringHelpers.title(
				fields?.archived 
				|| 'Archived'
			),
			value: formatDate(
				data.deleted_at 
				|| '-'
			),
			sortable: true,
			visibleCondition: () => 
				!!searchParams.get('filter[trashed]'),
		} 
		const actionsColumn: DataColumnInterface =
		{
			id: 'actions',
			label: StringHelpers.title(
				fields?.actions 
				|| 'Actions'
			),
			value: data.permissions.view ? (
				<FormInput
					type='select'
					label={StringHelpers.title(
						fields?.actions 
						|| 'Action'
					)}
					placeholder={StringHelpers.title(
						fields?.select 
						|| 'Select'
					)}
					name='actions'
					value=''
					onChange={(value: any) => {
						handleOptionSelect(value, data);
					}}
					options={[
						{
							label: StringHelpers.title(
								crud?.view 
								|| 'View'
							),
							value: 'view',
						},
					]}
					noMargin
				/>
			) : null,
		};
		
		const viessmannColumns: DataColumnInterface[] = [
			{
				id: 'completion_date',
				label: StringHelpers.title(fields?.date || 'Installation Date'),
				value: formatDateFns(data.completion_date, DateFormats.DATE) || '-',
				sortable: true,
			},
			{
				id: 'customer_name',
				label: StringHelpers.title(fields?.customer_name || 'Customer Name'),
				value: data?.customer_name || '-',
				sortable: true,
			},
			{
				id: 'address_line_1',
				label: StringHelpers.title(fields?.address || 'Address'),
				value: data?.address_line_1 || '-',
				sortable: true,
			},
			{
				id: 'postcode_zipcode',
				label: StringHelpers.title(fields?.postcode_zipcode || 'Postcode'),
				value: data?.postcode_zipcode || '-',
				sortable: true,
			},
			{
				id: 'appliance_product_name',
				label: StringHelpers.title(fields?.appliance_product_name || 'Product Name'),
				value: data?.appliance_product_name || '-',
				sortable: false,
			},
			{
				id: 'appliances_serial_number',
				label: StringHelpers.title(fields?.appliances_serial_number || 'Serial Number'),
				value: data?.appliances_serial_number || '-',
				sortable: false,
			},
			{
				id: 'warranty_length',
				label: StringHelpers.title(fields?.warranty_length || 'Warranty Length'),
				value: data?.warranty_length || '-',
				sortable: true,
			},
			statusColumn,
			deletedColumn,
			actionsColumn,
		];

		const toshibaColumns: DataColumnInterface[] = [
			{
				id: 'customer_name',
				label: StringHelpers.title(
					fields?.customer_name
					|| 'Customer Name'
				),
				value: data?.customer_name
					|| '-',
				sortable: true,
			},
			{
				id: 'address',
				label: StringHelpers.title(
					fields?.address
					|| 'Address'
				),
				value: addressDisplay,
				sortable: false,
			},
			{
				id: 'installer_company_name',
				label: crud?.sub_titles?.installer
					|| 'Installer',
				value: data?.installer_company_name
					|| '-',
				sortable: true,
				visibleCondition: () =>
					hasRoleAccess(Roles.SUPER_ADMIN)
					|| hasRoleAccess(Roles.PLATFORM_MANAGER)
					|| hasRoleAccess(Roles.DISTRIBUTION_MANAGER),
			},
			{
				id: 'distributor_name',
				label: crud?.sub_titles?.distributor
					|| 'Distributor',
				value: data?.distributor_name
					|| '-',
				sortable: true,
			},
			{
				id: 'engineer_name',
				label: crud?.models?.engineer
					|| 'Engineer',
				value: data?.engineer_name
					|| '-',
				sortable: true,
			},
			{
				id: 'created_at',
				label: StringHelpers.title(
					fields?.created_at
					|| '-'
				),
				value: formatDateFns(data?.created_at, DateFormats.DATETIME_SHORT)
					|| '-',
				sortable: true
			},
			deletedColumn,
			actionsColumn,
		];

		return checkTheme(Themes.Viessmann) ? viessmannColumns : toshibaColumns; 
	};

	const dropDownOptions = (): DropdownItemProps[] => {
		if (
			!checkTheme(Themes.Toshiba)
			|| !hasPermissionAccess(Permissions.TOSHIBA_INSTALLATION_STORE)
		) {
			return [];
		}

		return [
			{
				label: StringHelpers.title(crud?.buttons?.create?.default || 'Create'),
				icon: <Icon icon={Icons.ADD} />,
				onClick: () => navigate('/installation/create'),
			},
		];
	}; 

	const filters: FormInputType[] = [
		{
			name: 'status',
			label: StringHelpers.title(fields?.status),
			type: 'searchable-select',
			value: '',
			placeholder: StringHelpers.title(
                fields?.please_select 
                || 'please select'
            ),
			options: crud?.options?.installation_status,
		}
	];

	return (
		<PageLayout title={registration?.navigation?.registration?.installations || 'My Installations'}>
			<DataTable<ViessmannInstallationListItem & ToshibaInstallationListItem>
				routeEndpoint={route(
					API_ENDPOINTS.REGISTRATION.INSTALLATION.INDEX
				)}
				colMapping={colMapping}
				incrementPaginationButtons
				variant={Variants.Info}
				canViewArchived
				items={dropDownOptions()}
				filters={checkTheme(Themes.Toshiba) ? undefined : filters}
			/>
		</PageLayout>
	);
}

export default InstallationList;
